import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React, { useEffect, useState } from 'react';
import { useOrg } from '../organization/useOrg';
import { FiscalYear, FiscalYearFormData } from '../types/fiscalYear';
import { useCreateFiscalYear, useUpdateFiscalYear } from './useFiscalYears';

interface FiscalYearEditModalProps {
  open: boolean;
  onClose: () => void;
  editingFiscalYear: FiscalYear | null;
  existingFiscalYears?: FiscalYear[];
}

export function FiscalYearEditModal({
  open,
  onClose,
  editingFiscalYear,
  existingFiscalYears = [],
}: FiscalYearEditModalProps) {
  const { selectedOrg } = useOrg();
  const hasFiscalYearStart = !!selectedOrg?.fiscalYearStart;
  const createMutation = useCreateFiscalYear(selectedOrg?.id || '');
  const updateMutation = useUpdateFiscalYear(selectedOrg?.id || '');

  const [formData, setFormData] = useState<FiscalYearFormData>({
    name: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
      .toISOString()
      .split('T')[0],
    isActive: false,
  });

  const [touched, setTouched] = useState({
    name: false,
    startDate: false,
    endDate: false,
  });

  useEffect(() => {
    if (editingFiscalYear) {
      setFormData({
        name: editingFiscalYear.name,
        startDate: editingFiscalYear.startDate,
        endDate: editingFiscalYear.endDate,
        isActive: editingFiscalYear.isActive,
      });
    } else {
      setFormData({
        name: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
          .toISOString()
          .split('T')[0],
        isActive: existingFiscalYears.length === 0, // First fiscal year is active by default
      });
    }
    setTouched({ name: false, startDate: false, endDate: false });
  }, [editingFiscalYear, existingFiscalYears]);

  const handleFieldChange =
    (field: keyof FiscalYearFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({
        ...formData,
        [field]:
          field === 'isActive' ? event.target.checked : event.target.value,
      });
    };

  const handleDateChange =
    (field: 'startDate' | 'endDate') => (date: Date | null) => {
      if (date) {
        setFormData({
          ...formData,
          [field]: date.toISOString().split('T')[0],
        });
      }
    };

  const handleBlur = (field: keyof typeof touched) => () => {
    setTouched({ ...touched, [field]: true });
  };

  const isValid = () => {
    if (!formData.name.trim()) return false;
    if (hasFiscalYearStart) return true;
    return (
      !!formData.startDate &&
      !!formData.endDate &&
      new Date(formData.startDate) < new Date(formData.endDate)
    );
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    try {
      if (editingFiscalYear) {
        await updateMutation.mutateAsync({
          fyId: editingFiscalYear.id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save fiscal year:', error);
    }
  };

  const nameError =
    touched.name && !formData.name.trim() ? 'Name is required' : '';

  const dateError =
    touched.endDate &&
    new Date(formData.startDate) >= new Date(formData.endDate)
      ? 'End date must be after start date'
      : '';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>
          {editingFiscalYear ? 'Edit Fiscal Year' : 'Add Fiscal Year'}
        </DialogTitle>
        <DialogContent>
          <Stack>
            <TextField
              label='Name'
              id='fiscal-year-name'
              name='fiscal-year-name'
              value={formData.name}
              onChange={handleFieldChange('name')}
              onBlur={handleBlur('name')}
              error={!!nameError}
              helperText={nameError}
              fullWidth
              required
            />

            {!hasFiscalYearStart && (
              <>
                <DatePicker
                  label='Start Date'
                  value={new Date(formData.startDate)}
                  onChange={handleDateChange('startDate')}
                  slotProps={{
                    textField: {
                      id: 'fiscal-year-start-date',
                      name: 'fiscal-year-start-date',
                      fullWidth: true,
                      required: true,
                      onBlur: handleBlur('startDate'),
                    },
                  }}
                />

                <DatePicker
                  label='End Date'
                  value={new Date(formData.endDate)}
                  onChange={handleDateChange('endDate')}
                  slotProps={{
                    textField: {
                      id: 'fiscal-year-end-date',
                      name: 'fiscal-year-end-date',
                      fullWidth: true,
                      required: true,
                      error: !!dateError,
                      helperText: dateError,
                      onBlur: handleBlur('endDate'),
                    },
                  }}
                />
              </>
            )}

            {existingFiscalYears.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    id='fiscal-year-is-active'
                    name='fiscal-year-is-active'
                    checked={formData.isActive}
                    onChange={handleFieldChange('isActive')}
                  />
                }
                label='Set as active fiscal year'
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant='contained'
            disabled={
              !isValid() || createMutation.isPending || updateMutation.isPending
            }
          >
            {editingFiscalYear ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
